/**
 * Content Control
 *
 * Unified admin page for managing mobile app content and layout.
 * Combines banner and category management with visual preview.
 *
 * Features:
 * - Global draft system (all admins share one draft)
 * - Visual preview matching mobile app layout
 * - Three view modes: Mobile Home, Web Home, Shop Page
 * - Store-specific preview filtering
 * - Publish/rollback capability with history
 * - Collaboration presence awareness
 * - Section-focused editing
 *
 * @module ContentControl
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Skeleton,
  Alert,
  AlertTitle,
  Grid,
  Chip,
  Avatar,
  AvatarGroup,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress,
  Switch,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Publish as PublishIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CompareArrows as CompareIcon,
  MenuBook as MenuBookIcon,
  ArrowBack as BackIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';

import { HomeLayoutProvider, useHomeLayout } from './HomeLayoutContext';
import ConfigurationPanel, { type ConfigTab } from './components/ConfigurationPanel';
import SectionHub, { SECTIONS } from './components/SectionHub';
import PreviewPanel from './components/PreviewPanel';
import PublishHistoryDialog from './components/PublishHistoryDialog';
import PublishReviewDialog from './components/PublishReviewDialog';
import DocumentationPanel from './components/DocumentationPanel';
import { fetchBrands } from './api';
import type { HubBrand } from './components/SectionHub';
import { APP_BAR_HEIGHT } from '../../admin-theme';

/**
 * Format relative time from ISO date string
 */
function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

  return date.toLocaleDateString();
}

/**
 * Page skeleton shown during initial load
 */
function PageSkeleton(): React.ReactElement {
  return (
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width={280} height={40} />
      <Skeleton variant="text" width={180} height={20} sx={{ mb: 2 }} />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          {/* Config panel skeleton */}
          <Skeleton variant="rounded" height={80} sx={{ mb: 2 }} />
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 1 }} />
          ))}
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          {/* Preview skeleton with phone frame */}
          <Skeleton variant="rounded" height={500} sx={{ borderRadius: 4 }} />
        </Grid>
      </Grid>
    </Box>
  );
}

/**
 * Error state component
 */
interface ErrorStateProps {
  /** Error message to display */
  message: string;
  /** Callback to retry loading */
  onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps): React.ReactElement {
  return (
    <Box sx={{ p: 3 }}>
      <Alert
        severity="error"
        sx={{ borderRadius: 2, border: '1px solid', borderColor: 'error.light' }}
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Try Again
          </Button>
        }
      >
        <AlertTitle>Failed to load layout data</AlertTitle>
        {message}
      </Alert>
    </Box>
  );
}

/**
 * Publish confirmation dialog
 */
interface PublishDialogProps {
  /** Whether dialog is open */
  open: boolean;
  /** Close callback */
  onClose: () => void;
  /** Confirm publish callback with notes */
  onConfirm: (notes: string) => void;
  /** Whether publish is in progress */
  isPublishing: boolean;
  /** Number of changes to publish */
  changesCount: number;
}

function PublishDialog({
  open,
  onClose,
  onConfirm,
  isPublishing,
  changesCount,
}: PublishDialogProps): React.ReactElement {
  const [notes, setNotes] = useState('');

  const handleConfirm = useCallback(() => {
    onConfirm(notes);
    setNotes('');
  }, [notes, onConfirm]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PublishIcon color="success" />
        Publish Changes
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          You are about to publish <strong>{changesCount} change{changesCount === 1 ? '' : 's'}</strong> to the
          live home page. These changes will be visible to all app users immediately.
        </DialogContentText>
        <TextField
          autoFocus
          label="Publish notes (optional)"
          placeholder="e.g., Updated holiday banners"
          fullWidth
          multiline
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isPublishing}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPublishing}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="success"
          disabled={isPublishing}
          startIcon={isPublishing ? <CircularProgress size={16} /> : <PublishIcon />}
        >
          {isPublishing ? 'Publishing...' : 'Publish Live'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Reset confirmation dialog
 */
interface ResetDialogProps {
  /** Whether dialog is open */
  open: boolean;
  /** Close callback */
  onClose: () => void;
  /** Confirm reset callback */
  onConfirm: () => void;
  /** Whether reset is in progress */
  isResetting: boolean;
}

function ResetDialog({
  open,
  onClose,
  onConfirm,
  isResetting,
}: ResetDialogProps): React.ReactElement {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" />
        Discard Changes?
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          This will discard all unpublished changes and reset the draft to match the current live
          state. This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isResetting}>
          Keep Editing
        </Button>
        <Button
          onClick={onConfirm}
          color="warning"
          disabled={isResetting}
          startIcon={isResetting ? <CircularProgress size={16} /> : <RefreshIcon />}
        >
          {isResetting ? 'Resetting...' : 'Discard Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Main content component (uses context)
 */
function HomeLayoutManagerContent(): React.ReactElement {
  const {
    draft,
    liveState,
    isLoading,
    isSaving,
    isPublishing,
    error,
    lastModifiedBy,
    lastModifiedAt,
    hasChanges,
    changes,
    activeUsers,
    compareMode,
    selectedStoreId,
    pendingOperations,
    meaningfulChangeCount,
    resetDraft,
    publish,
    refreshDraft,
    setCompareMode,
    publishNotification,
    dismissPublishNotification,
  } = useHomeLayout();

  // Dialog and panel states
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  /** null = the section hub; otherwise the section being edited. */
  const [section, setSection] = useState<ConfigTab | null>(null);
  /**
   * Whether categories with no products at the selected store still show in the
   * preview (greyed out, in position). Lives here rather than inside PreviewPanel
   * because the switch sits on this side, above the section you're editing.
   */
  // Default OFF. On meant the editor listed categories and brands that have nothing at
  // the selected store — ZYN, Vozol, E-XHALE all showing "No products at this store" —
  // while the preview correctly omitted them, so the two sides disagreed and the
  // preview looked broken when it was the only one telling the truth. Off by default,
  // the list shows what the app shows; turn it on deliberately to go hunting for gaps.
  const [showEmptyCategories, setShowEmptyCategories] = useState(false);

  const activeSection = section ? SECTIONS.find((s) => s.key === section) : null;

  /**
   * Live brands, purely so the hub's Brands card can count what its section
   * actually lists. `fetchBrands` is memoised in api.ts, so the Brands tab
   * opening later doesn't pay for this again.
   */
  const [hubBrands, setHubBrands] = useState<HubBrand[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchBrands().then((data) => {
      if (!cancelled) setHubBrands(data);
    });
    return () => { cancelled = true; };
  }, []);

  // Change counts per section, so a hub card can say it has unpublished edits.
  const changedSections = useMemo(() => {
    if (!changes) return {};
    const bySection: Partial<Record<ConfigTab, number>> = {};
    if (changes.banners?.length) bySection.banners = changes.banners.length;
    if (changes.brands?.length) bySection.brands = changes.brands.length;
    if (changes.categories?.length) {
      // A category edit can land on any of the three category surfaces; without
      // per-surface attribution, flag all three rather than guess.
      const n = changes.categories.length;
      bySection.home = n;
      bySection.carousel = n;
      bySection.shop = n;
      bySection.all = n;
    }
    return bySection;
  }, [changes]);

  // Handle publish
  const handlePublish = useCallback(async (notes: string) => {
    try {
      await publish(notes);
      setPublishDialogOpen(false);
    } catch (err) {
      // Error is handled by the hook
    }
  }, [publish]);

  // Handle reset - categories created via CategoryFormDialog exist in DB
  // and will be picked up by createDraftFromLiveState + syncDraftWithDatabase
  const handleReset = useCallback(async () => {
    try {
      await resetDraft();
      setResetDialogOpen(false);
    } catch (err) {
      // Error is handled by the hook
    }
  }, [resetDraft]);

  // Loading state
  if (isLoading && !draft) {
    return <PageSkeleton />;
  }

  // Error state
  if (error && !draft) {
    return <ErrorState message={error} onRetry={refreshDraft} />;
  }

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.02em',
              mb: 0.5,
            }}
          >
            Content Control
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {lastModifiedBy ? `Last modified by ${lastModifiedBy.name}` : 'No modifications yet'}
              {lastModifiedAt && ` • ${formatRelativeTime(lastModifiedAt)}`}
            </Typography>
            {hasChanges && (
              <Chip
                label={`${meaningfulChangeCount} unpublished`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Active users avatars */}
          {activeUsers.length > 0 && (
            <AvatarGroup max={3} sx={{ mr: 1 }}>
              {activeUsers.map((presence, index) => {
                const userName = presence.user?.name ?? 'Unknown';
                const avatarUrl = presence.user?.avatarUrl;
                const isEditing = !!presence.editingItemId;
                const section = presence.viewingSection ?? 'page';
                const tooltipText = isEditing
                  ? `${userName} is editing in ${section}`
                  : `${userName} viewing ${section}`;

                return (
                  <Tooltip key={index} title={tooltipText}>
                    <Avatar
                      src={avatarUrl || undefined}
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: 12,
                        bgcolor: isEditing ? 'warning.main' : 'primary.main',
                        // Pulse animation when editing
                        animation: isEditing ? 'pulse 2s infinite' : 'none',
                        '@keyframes pulse': {
                          '0%': { boxShadow: '0 0 0 0 rgba(237, 108, 2, 0.4)' },
                          '70%': { boxShadow: '0 0 0 6px rgba(237, 108, 2, 0)' },
                          '100%': { boxShadow: '0 0 0 0 rgba(237, 108, 2, 0)' },
                        },
                      }}
                    >
                      {/* Show initial only if no avatar URL */}
                      {!avatarUrl && userName.charAt(0)}
                    </Avatar>
                  </Tooltip>
                );
              })}
            </AvatarGroup>
          )}

          {/* Documentation toggle */}
          <Tooltip title={showDocs ? 'Hide documentation' : 'Show documentation'}>
            <IconButton
              onClick={() => setShowDocs(!showDocs)}
              color={showDocs ? 'primary' : 'default'}
              sx={{
                border: 1,
                borderColor: showDocs ? 'primary.main' : 'divider',
              }}
            >
              <MenuBookIcon />
            </IconButton>
          </Tooltip>

          {/* Compare mode toggle */}
          <Tooltip title={compareMode ? 'Exit compare mode' : 'Compare draft vs live'}>
            <IconButton
              onClick={() => setCompareMode(!compareMode)}
              color={compareMode ? 'primary' : 'default'}
              sx={{
                border: 1,
                borderColor: compareMode ? 'primary.main' : 'divider',
              }}
            >
              <CompareIcon />
            </IconButton>
          </Tooltip>

          {/* History button */}
          <Tooltip title="View publish history">
            <IconButton
              onClick={() => setHistoryDialogOpen(true)}
              sx={{ border: 1, borderColor: 'divider' }}
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>

          {/* Reset button */}
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<RefreshIcon />}
            onClick={() => setResetDialogOpen(true)}
            disabled={!hasChanges || isSaving}
          >
            Reset Draft
          </Button>

          {/* Publish button */}
          <Button
            variant="contained"
            color="success"
            startIcon={isPublishing ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
            onClick={() => setPublishDialogOpen(true)}
            disabled={!hasChanges || isPublishing || isSaving}
            sx={{
              background: 'linear-gradient(135deg, #10B981, #059669)',
              boxShadow: '0 4px 14px rgba(16, 185, 129, 0.25)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(16, 185, 129, 0.35)',
              },
            }}
          >
            Publish Live
          </Button>
        </Box>
      </Box>

      {/* Main content layout - fixed height container with side-by-side panels */}
      <Box sx={{ display: 'flex', gap: 3, height: 'calc(100vh - 180px)' }}>
        {/* Work area: the section hub, or one section's editor. */}
        <Box sx={{ width: '58%', flexShrink: 0, height: '100%', overflow: section ? 'hidden' : 'auto', display: 'flex', flexDirection: 'column' }}>
          {/* Empty-category switch. It governs the preview but belongs on this
              side: it is a property of the layout you are editing, and it sat in
              the preview column pushing the phone down the page. Only meaningful
              once a store is chosen — with no store there is no "empty here". */}
          {selectedStoreId && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary">
                Show empty categories
              </Typography>
              <Switch
                size="small"
                checked={showEmptyCategories}
                onChange={(e) => setShowEmptyCategories(e.target.checked)}
              />
              <Tooltip title="When enabled, categories with no products at this store appear greyed out in their correct position">
                <InfoIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
          )}
          {section ? (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexShrink: 0 }}>
                <Button
                  size="small"
                  startIcon={<BackIcon />}
                  onClick={() => setSection(null)}
                  sx={{ textTransform: 'none' }}
                >
                  Sections
                </Button>
                <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                  {activeSection?.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
                  {activeSection?.what}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <ConfigurationPanel
                  activeTab={section}
                  onTabChange={setSection}
                  hideStoreSelector
                  hideTabs
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <SectionHub draft={draft} onOpen={setSection} changedSections={changedSections} brands={hubBrands} />
            </Box>
          )}
        </Box>

        {/* Preview panel - 40% - scrolls independently */}
        <Box sx={{ flex: 1, height: '100%', overflow: 'auto' }}>
          {/* Told which section is open so it can point itself at the part of
              the app you're actually editing. */}
          <PreviewPanel section={section} showEmptyCategories={showEmptyCategories} />
        </Box>

        {/* Documentation Panel - slide in from right */}
        {showDocs && (
          <Box
            sx={{
              width: 400,
              flexShrink: 0,
              height: 'calc(100vh - 200px)',
              position: 'sticky',
              // Clear the fixed 56px app bar, then the usual 24px gutter.
              top: APP_BAR_HEIGHT + 24,
            }}
          >
            <DocumentationPanel onClose={() => setShowDocs(false)} />
          </Box>
        )}
      </Box>

      {/* Dialogs */}
      <PublishReviewDialog
        open={publishDialogOpen}
        onClose={() => setPublishDialogOpen(false)}
        onConfirm={handlePublish}
        isPublishing={isPublishing}
        changes={changes}
        categories={draft?.categoryConfig?.categories ?? null}
        bannerItems={
          draft?.bannerConfig?.positions?.flatMap((p) => p.items) ?? null
        }
        brandPositions={draft?.brandPositions?.positions ?? null}
        pendingOperations={pendingOperations}
        meaningfulChangeCount={meaningfulChangeCount}
      />

      <ResetDialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        onConfirm={handleReset}
        isResetting={isSaving}
      />

      <PublishHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
      />

      {/* Publish notification dialog (from another admin) */}
      <Dialog open={!!publishNotification} onClose={dismissPublishNotification} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PublishIcon color="info" />
          Layout Updated
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {publishNotification?.publishedByName} just published changes. Your draft has been refreshed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={dismissPublishNotification} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/**
 * Home Layout Manager Page Component
 *
 * Main entry point for the unified Home Layout Manager.
 * Wraps content in the HomeLayoutProvider context.
 *
 * @returns The Home Layout Manager page
 *
 * @example
 * ```tsx
 * // In routes
 * <Route path="/layout/home" element={<HomeLayoutManager />} />
 * ```
 */
export default function HomeLayoutManager(): React.ReactElement {
  return (
    <HomeLayoutProvider>
      <HomeLayoutManagerContent />
    </HomeLayoutProvider>
  );
}
